import {DrivingInfoDistanceProps} from '@di-shared/features/history/driving-info/DrivingInfoDistance/index';
import {ErrorBoundary} from 'react-error-boundary';
import React, {Suspense, useState} from 'react';
import useDrivingInfoKr, {getHistoryForGraph} from '@di-shared/features/history/driving-info/useDrivingInfo.kr';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {addDate, isSameYearAndMonth, startDateOfMonth, subDate} from '@shared/dateUtils';
import {formatNumberWithCommas} from '@di-shared/utils';
import {ERROR} from '@di-shared/const';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import useAvailableComponentKr, {SeoulMileageSupportedType} from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import {mapValueToRange} from '@di-shared/utils/graph';
import clsx from 'clsx';
import DrivingInfoEcoMileageBanner from '@di-shared/features/history/driving-info/DrivingInfoEcoMileageBanner';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

export const DRIVING_DISTANCE_GRAPH_KR = {
  MIN_VALUE: 0,
  MAX_HEIGHT: 90,
  MIN_HEIGHT: 6,
  AREA_FULL: 100,
  AREA_HALF: 50,
  LENGTH: 5,
};

export default function ({vin, selectedDate}: DrivingInfoDistanceProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          key={getYYYYMMFormat(selectedDate)}
          vin={vin}
          selectedDate={selectedDate}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate}: DrivingInfoDistanceProps) {
  const {isSameCcsAgreeMonth} = useDrivingInfoKr({vin, selectedDate});

  // 가입 당월 화면
  if (isSameCcsAgreeMonth) {
    return <Agree vin={vin} />;
  }

  // Default 화면
  return (
    <Default
      vin={vin}
      selectedDate={selectedDate}
    />
  );
}

function Agree({vin}: {vin: string}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {drivingData, currentDrivingDistance} = useDrivingInfoKr({vin});
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4 className="title_xsmall_b">{t('T10025_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h4>
      </div>
      <ul className="statistics_summary_list">
        <li className="statistics_summary_item">
          <span className="statistics_summary_key label_large_r">
            {t('T10026_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}
          </span>
          <span className="statistics_summary_val">
            <span className="title_small_b">{formatNumberWithCommas(convertDistanceValue(currentDrivingDistance.totalDistance))}</span>
            <span className="statistics_summary_unit label_small_b">{convertDistanceSymbol(currentDrivingDistance.totalDistance)}</span>
          </span>
        </li>
        <li className="statistics_summary_item">
          <span className="statistics_summary_key label_large_r">{t('T10033')}</span>
          <span className="statistics_summary_val">
            <span className="title_small_b">{formatNumberWithCommas(convertDistanceValue(drivingData.odometer))}</span>
            <span className="statistics_summary_unit label_small_b">{convertDistanceSymbol(drivingData.odometer)}</span>
          </span>
        </li>
      </ul>
      <div className="driving_banner">
        <a
          role="button"
          className="driving_banner_box"
        >
          <span className="driving_banner_txtbox">
            <span
              className="driving_banner_desc body_small_r"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {currentDrivingDistance.totalDistance === 0
                ? t('T10034')
                : t('T10035_$month', {month: CldrDatetimeFormatter.format(addDate(today, {months: 1}), 'MMM')})}
            </span>
          </span>
          <span className="driving_banner_imgbox">
            <ImageSelector
              className="driving_banner_img"
              alt="driving_banner_img"
              kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_calendar.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_calendar.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_calendar.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_calendar.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_calendar.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_calendar.png')}
            />
          </span>
        </a>
      </div>
    </div>
  );
}

function Default({vin, selectedDate}: DrivingInfoDistanceProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const {seoulMileageSupportedType} = useAvailableComponentKr({vin});
  const {drivingData, verifiedCcsAgreeDate, verifiedDrivingDistances, currentDrivingDistance, prevDrivingDistance} = useDrivingInfoKr({
    vin,
    selectedDate,
  });
  const [selectedBarGraph, setSelectedBarGraph] = useState(getYYYYMMFormat(selectedDate));
  const availableDrivingDistances = getHistoryForGraph(verifiedDrivingDistances, selectedDate, DRIVING_DISTANCE_GRAPH_KR.LENGTH);

  const isDrivingDistanceExist = drivingData.avgDistance !== 0;
  const isPrevDistanceExist = !!prevDrivingDistance;
  const distanceDiff = isPrevDistanceExist ? currentDrivingDistance.totalDistance - prevDrivingDistance.totalDistance : 0;
  const isDistanceDiffUp = isPrevDistanceExist ? distanceDiff >= 0 : true;

  const maxDistance = Math.max(drivingData.avgDistance, ...availableDrivingDistances.map((driving) => driving.totalDistance));
  const averageDistanceHeight = isDrivingDistanceExist
    ? DRIVING_DISTANCE_GRAPH_KR.AREA_FULL -
      mapValueToRange({
        value: drivingData.avgDistance,
        minValue: DRIVING_DISTANCE_GRAPH_KR.MIN_VALUE,
        maxValue: maxDistance,
        minRange: DRIVING_DISTANCE_GRAPH_KR.MIN_HEIGHT,
        maxRange: DRIVING_DISTANCE_GRAPH_KR.MAX_HEIGHT,
      })
    : DRIVING_DISTANCE_GRAPH_KR.AREA_HALF;

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4 className="title_xsmall_b">
          {t('T10038_$month_$dist', {
            month: CldrDatetimeFormatter.format(selectedDate, 'MMM'),
            dist:
              formatNumberWithCommas(convertDistanceValue(currentDrivingDistance.totalDistance)) +
              convertDistanceSymbol(currentDrivingDistance.totalDistance),
          })}
        </h4>
      </div>
      {isDrivingDistanceExist && isPrevDistanceExist && (
        <div className="statistics_compare">
          <span className="statistics_compare_key label_medium_r">
            {t('T10039_$month', {month: CldrDatetimeFormatter.format(subDate(selectedDate, {months: 1}), 'MMM')})}
          </span>
          <span
            className={clsx('statistics_compare_val', {
              type_up: isDistanceDiffUp,
              type_down: !isDistanceDiffUp,
            })}
          >
            <span
              className={clsx('ic', {ic_di_count_up: isDistanceDiffUp, ic_di_count_down: !isDistanceDiffUp})}
              aria-hidden="true"
            >
              <span className="blind">{isDistanceDiffUp ? t('T10059') : t('T10058')}</span>
            </span>
            <span className="label_medium_b">
              {formatNumberWithCommas(convertDistanceValue(Math.abs(distanceDiff)))}
              {convertDistanceSymbol(Math.abs(distanceDiff))}
            </span>
          </span>
        </div>
      )}
      <div
        className="statistics_graph_area distance_statistics"
        aria-hidden="true"
        tabIndex={-1}
      >
        <div className="bar_graph">
          {availableDrivingDistances.length < DRIVING_DISTANCE_GRAPH_KR.LENGTH &&
            Array.from({length: DRIVING_DISTANCE_GRAPH_KR.LENGTH - availableDrivingDistances.length}).map((_, i) => {
              const legendDate = subDate(verifiedCcsAgreeDate, {
                months: DRIVING_DISTANCE_GRAPH_KR.LENGTH - availableDrivingDistances.length - i,
              });
              const isSelectedMonth = getYYYYMMFormat(legendDate) === selectedBarGraph;
              return (
                <div
                  key={getYYYYMMFormat(legendDate)}
                  className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
                  onClick={() => setSelectedBarGraph(getYYYYMMFormat(legendDate))}
                >
                  {isSelectedMonth && <span className="speech_bubble">{0}</span>}
                  <span
                    className="graph_bar"
                    style={{height: '0px'}}
                  />
                  <span className="graph_title">{CldrDatetimeFormatter.format(legendDate, 'MMM')}</span>
                </div>
              );
            })}
          {availableDrivingDistances.map((driving) => {
            const isSelectedMonth = driving.date === selectedBarGraph;
            const graphHeight = mapValueToRange({
              value: driving.totalDistance,
              minValue: DRIVING_DISTANCE_GRAPH_KR.MIN_VALUE,
              maxValue: maxDistance,
              minRange: DRIVING_DISTANCE_GRAPH_KR.MIN_HEIGHT,
              maxRange: DRIVING_DISTANCE_GRAPH_KR.MAX_HEIGHT,
            });
            return (
              <div
                key={driving.date}
                className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
                onClick={() => setSelectedBarGraph(driving.date)}
              >
                {isSelectedMonth && <span className="speech_bubble">{formatNumberWithCommas(driving.totalDistance)}</span>}
                <span
                  className="graph_bar"
                  style={{height: `${graphHeight}px`}}
                />
                <span className="graph_title">{CldrDatetimeFormatter.format(driving.verifiedDate, 'MMM')}</span>
              </div>
            );
          })}
          <div
            className="bar_graph_average"
            style={{top: `${averageDistanceHeight}px`}}
          >
            <span className="average_line" />
            <span className="average_text_box">
              <span className="average_text">{t('T10083')}</span>
              <span className="average_value">{formatNumberWithCommas(drivingData.avgDistance)}</span>
            </span>
          </div>
        </div>
      </div>
      {seoulMileageSupportedType === SeoulMileageSupportedType.SUPPORTED_AGREE && (
        <DrivingInfoEcoMileageBanner
          vin={vin}
          averageDistance={drivingData.avgDistance}
        />
      )}
    </div>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol} = useUnitConverter();
  const today = new Date();
  const errorDrivingDistances = Array.from(Array(DRIVING_DISTANCE_GRAPH_KR.LENGTH).keys()).reverse();

  return (
    <div className="section_container skeleton">
      <div className="section_container_header">
        <h4 className="title_xsmall_b">
          {t('T10038_$month_$dist', {
            month: CldrDatetimeFormatter.format(today, 'MMM'),
            dist: ERROR.DASH + defaultDistanceSymbol,
          })}
        </h4>
      </div>
      <div className="statistics_graph_area distance_statistics">
        <div className="bar_graph">
          {errorDrivingDistances.map((month) => {
            const legendDate = subDate(today, {months: month});
            const isSelectedMonth = isSameYearAndMonth(legendDate, today);
            return (
              <div
                key={month}
                className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
              >
                {isSelectedMonth && <span className="speech_bubble">{ERROR.DASH}</span>}
                <span
                  className="graph_bar"
                  style={{height: `${DRIVING_DISTANCE_GRAPH_KR.MIN_HEIGHT}px`}}
                />
                <span className="graph_title">{CldrDatetimeFormatter.format(legendDate, 'MMM')}</span>
              </div>
            );
          })}
          <div
            className="bar_graph_average"
            style={{top: `${DRIVING_DISTANCE_GRAPH_KR.AREA_HALF}px`}}
          >
            <span className="average_line" />
            <span className="average_text_box">
              <span className="average_text">{t('T10083')}</span>
              <span className="average_value">{ERROR.DASH}</span>
            </span>
          </div>
        </div>
      </div>
      <div className="driving_banner">
        <div className="driving_banner_box">
          <span className="driving_banner_txtbox">
            <strong className="driving_banner_title body_medium_b">{t('T10068')}</strong>
            <span className="driving_banner_desc body_small_r">{t('T10069_$mileage', {mileage: 6})}</span>
          </span>
          <span className="driving_banner_imgbox">
            <ImageSelector
              className="driving_banner_img"
              alt="driving_banner_img"
              kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage.png')}
            />
          </span>
        </div>
      </div>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol} = useUnitConverter();
  const startDate = startDateOfMonth(selectedDate);
  const errorDrivingDistances = Array.from(Array(DRIVING_DISTANCE_GRAPH_KR.LENGTH).keys()).reverse();

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4 className="title_xsmall_b">
          {t('T10038_$month_$dist', {
            month: CldrDatetimeFormatter.format(startDate, 'MMM'),
            dist: ERROR.DASH + defaultDistanceSymbol,
          })}
        </h4>
      </div>
      <div className="statistics_graph_area distance_statistics">
        <div className="bar_graph">
          {errorDrivingDistances.map((month) => {
            const legendDate = subDate(startDate, {months: month});
            const isSelectedMonth = isSameYearAndMonth(legendDate, startDate);
            return (
              <div
                key={month}
                className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
              >
                {isSelectedMonth && <span className="speech_bubble">{ERROR.DASH}</span>}
                <span
                  className="graph_bar"
                  style={{height: `${DRIVING_DISTANCE_GRAPH_KR.MIN_HEIGHT}px`}}
                />
                <span className="graph_title">{CldrDatetimeFormatter.format(legendDate, 'MMM')}</span>
              </div>
            );
          })}
          <div
            className="bar_graph_average"
            style={{top: `${DRIVING_DISTANCE_GRAPH_KR.AREA_HALF}px`}}
          >
            <span className="average_line" />
            <span className="average_text_box">
              <span className="average_text">{t('T10083')}</span>
              <span className="average_value">{ERROR.DASH}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
